import {
  AttachmentStrategy,
  attachmentStrategyDropdownOptions,
  attachmentStrategyToLabel,
  BETA_ATTACHMENT_STRATEGIES,
  CUSTOM_FEE_ATTACHMENT_STRATEGIES,
  DEPRECATED_ATTACHMENT_STRATEGIES,
  NOT_SHOPIFY_COMPLIANT_ATTACHMENT_STRATEGIES,
} from "@redotech/redo-storefront-common/attachment-strategy";
import type { StorefrontPlatform } from "@redotech/redo-storefront-common/storefront-config";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { Flex } from "@redotech/redo-web/flex";
import { FormSelectDropdown } from "@redotech/redo-web/select-dropdown";
import { Text } from "@redotech/redo-web/text";
import { Input } from "@redotech/ui/form";
import { memo } from "react";

interface AttachmentStrategyPickerProps {
  input: Input<AttachmentStrategy>;
  label?: string;
  description?: string;
  splitProducts: boolean;
  platform: StorefrontPlatform | undefined;
  placeholder?: string;
}

export const AttachmentStrategyPicker = memo(function AttachmentStrategyPicker({
  input,
  label = "Attachment strategy",
  description = "Placement in online store",
  splitProducts,
  platform,
  placeholder = "Default",
}: AttachmentStrategyPickerProps) {
  const dropdownOptions = attachmentStrategyDropdownOptions(
    splitProducts,
    platform,
  );

  return (
    <FormSelectDropdown
      description={description}
      input={input}
      label={label}
      options={[dropdownOptions]}
      placeholder={placeholder}
    >
      {(value) => <AttachmentStrategyLabel attachmentStrategy={value} />}
    </FormSelectDropdown>
  );
});

const AttachmentStrategyLabel = ({
  attachmentStrategy,
}: {
  attachmentStrategy: AttachmentStrategy;
}) => {
  const baseLabel = attachmentStrategyToLabel[attachmentStrategy];
  const labels = [];

  if (BETA_ATTACHMENT_STRATEGIES.includes(attachmentStrategy)) {
    labels.push(<RedoBadge color="blue" key="beta" size="xs" text="Beta" />);
  }
  if (DEPRECATED_ATTACHMENT_STRATEGIES.includes(attachmentStrategy)) {
    labels.push(
      <RedoBadge color="gray" key="deprecated" size="xs" text="Deprecated" />,
    );
  }
  if (
    NOT_SHOPIFY_COMPLIANT_ATTACHMENT_STRATEGIES.includes(attachmentStrategy)
  ) {
    labels.push(
      <RedoBadge
        color="orange"
        key="not-compliant"
        size="xs"
        text="Not Shopify compliant"
      />,
    );
  }
  if (CUSTOM_FEE_ATTACHMENT_STRATEGIES.includes(attachmentStrategy)) {
    labels.push(
      <RedoBadge
        color="pink"
        key="custom-fee-only"
        size="xs"
        text="BigCommerce / SFCC only"
      />,
    );
  }

  if (!labels.length) {
    return <>{baseLabel}</>;
  }

  return (
    <Flex align="center" gap="sm">
      <Text as="span">{baseLabel}</Text>
      {labels}
    </Flex>
  );
};
